import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { twJoin } from "tailwind-merge";

import type { AppBrandingDetailsDto } from "@/api/types";
import { DEFAULT_APP_BRANDING } from "@/helpers/app-branding";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { usePublicAppBranding } from "@/providers/PublicAppBrandingProvider";
import { useConfigurationQueries } from "@/queries/configurations/queryOptions";

type Props = {
  header: string;
  body?: React.ReactNode;
} & ({} | { step: number; totalSteps: number });

export function AuthenticationPage({
  header,
  body,
  children,
  ...props
}: React.PropsWithChildren<Props>): React.ReactNode {
  const { appBranding } = usePublicAppBranding();
  const configurationQueries = useConfigurationQueries();

  const {
    data: listAppBrandingConfigs,
    isError: isErrorListAppBrandingConfigs,
    isPending: isPendingListAppBrandingConfigs,
  } = useQuery(configurationQueries.listAppBrandings());

  useDocumentTitle(header);

  if (isPendingListAppBrandingConfigs || isErrorListAppBrandingConfigs) {
    return null;
  }

  const appBrandingConfig: AppBrandingDetailsDto =
    listAppBrandingConfigs.find((currAppBrandingConfig) => currAppBrandingConfig.id === appBranding) ||
    listAppBrandingConfigs.find((currAppBrandingConfig) => currAppBrandingConfig.id === DEFAULT_APP_BRANDING)!;

  return (
    <main className="flex h-screen md:items-center">
      <section className="mx-auto w-full grid-cols-1 sm:max-w-sm md:grid md:max-w-none md:grid-cols-2 md:px-space-md">
        <section className="flex w-full flex-col items-center gap-space-xl rounded-l-lg p-space-md md:max-w-xs md:justify-self-end md:bg-background-default md:p-space-xl">
          <span className="flex size-illustration-compact items-center justify-start">
            <img src={appBrandingConfig.logoImageUrl} className="block transition-width duration-200" alt="Logo" />
          </span>

          <AnimatePresence>
            {"step" in props && props.step && props.totalSteps ? (
              <motion.div
                className="flex w-full flex-row items-stretch gap-space-sm"
                initial={{ height: 0 }}
                animate={{ height: "auto" }}
                exit={{ height: 0 }}
              >
                {new Array(props.totalSteps).fill(undefined).map((_, i) => (
                  <div
                    aria-hidden
                    className={twJoin(
                      "h-space-xs flex-1 rounded-full transition-colors",
                      props.step > i ? "bg-background-brand-secondary" : "bg-background-tertiary",
                    )}
                    key={i}
                  />
                ))}
              </motion.div>
            ) : null}
          </AnimatePresence>

          <header className="flex w-full flex-col gap-space-sm *:text-center md:*:text-left">
            <h1 className="text-heading-1">{header}</h1>
            {body && (typeof body === "string" ? <p>{body}</p> : body)}
          </header>
          {children && <section className="relative w-full text-center md:text-left">{children}</section>}
        </section>
        <aside className="invisible hidden w-full max-w-xs justify-self-start rounded-r-lg bg-background-brand-light p-space-xl md:visible md:flex">
          <img className="aspect-square w-full object-contain" src={appBrandingConfig.bannerImageUrl} alt="" />
        </aside>
      </section>
    </main>
  );
}
